import { utils, writeFile } from "xlsx"
import type { TFunction } from "i18next"

import type {
  CouplingLoadView,
  UsinageDetail,
} from "@/features/milling/api/types"

import { buildRapport } from "./rapport"

// Usinage rapport workbook — four sheets matching the on-screen
// sections of the rapport synthèse:
//
//   1. Synthèse        — usinage meta + entrée / fabrication totals
//   2. Entrée Brousse  — per-chargement rows (Date col empty)
//   3. Fabrication     — per-lot rows (Date col empty)
//   4. Freinte         — per-planned-lot reconciliation + summary
export function downloadUsinageRapportXlsx(
  usinage: UsinageDetail,
  loads: CouplingLoadView[],
  t: TFunction<"milling">,
) {
  const rapport = buildRapport(usinage, loads)
  const wb = utils.book_new()

  const f = rapport.freinte
  const totalEntreeKg = rapport.entreeRows.reduce(
    (a, r) => a + r.poidsKg,
    0,
  )
  const totalFabricationKg = rapport.fabricationRows.reduce(
    (a, r) => a + r.poidsKg,
    0,
  )
  const synth = utils.aoa_to_sheet([
    [
      t("rapport.pdfTitle", {
        usinageNumber: rapport.usinageNumber,
        from: rapport.dateRange.from,
        to: rapport.dateRange.to,
        defaultValue: `USINAGE N° {{usinageNumber}} DU {{from}} au {{to}}`,
      }),
    ],
    [t("rapport.operator"), rapport.operator],
    [],
    [t("rapport.entreeBrousse"), "", "", t("rapport.fabrication")],
    [
      `${t("rapport.totalPoids")} (kg)`,
      Math.round(totalEntreeKg),
      "",
      `${t("rapport.totalPoids")} (kg)`,
      Math.round(totalFabricationKg),
    ],
    [],
    [t("rapport.freinteHeading")],
    [
      `${t("rapport.freinteSummary.totalPlanned")} (kg)`,
      Math.round(f.summary.totalPlannedKg),
    ],
    [
      `${t("rapport.freinteSummary.totalActual")} (kg)`,
      Math.round(f.summary.totalActualKg),
    ],
    [
      `${t("rapport.freinteSummary.waste")} (kg)`,
      Math.round(f.summary.wasteKg),
    ],
    [
      `${t("rapport.freinteSummary.ecart")} (kg)`,
      Math.round(f.summary.ecartKg),
    ],
    [
      `${t("rapport.freinteSummary.freinte")} (%)`,
      Number(f.summary.freintePct.toFixed(2)),
    ],
  ])
  utils.book_append_sheet(wb, synth, t("rapport.sheets.synthese"))

  const totalEntreeRefractKg = rapport.entreeRows.reduce(
    (a, r) => a + r.refractKg,
    0,
  )
  const entreeRows = [
    [
      t("rapport.cols.date"),
      t("rapport.cols.fournisseur"),
      t("rapport.cols.cccNumber"),
      t("rapport.cols.nature"),
      `${t("rapport.cols.poids")} (kg)`,
      `${t("rapport.cols.refract")} (kg)`,
    ],
    ...rapport.entreeRows.map((r) => [
      "",
      r.fournisseur,
      r.cccNumber,
      r.nature,
      Math.round(r.poidsKg),
      Math.round(r.refractKg),
    ]),
    [],
    [
      t("rapport.total"),
      "",
      "",
      "",
      Math.round(totalEntreeKg),
      Math.round(totalEntreeRefractKg),
    ],
  ]
  utils.book_append_sheet(
    wb,
    utils.aoa_to_sheet(entreeRows),
    t("rapport.sheets.entree"),
  )

  const fabRows = [
    [
      t("rapport.cols.date"),
      t("rapport.cols.numeroLot"),
      `${t("rapport.cols.poids")} (kg)`,
      t("rapport.cols.typeProduit"),
    ],
    ...rapport.fabricationRows.map((r) => [
      "",
      r.numeroLot,
      Math.round(r.poidsKg),
      r.typeProduit,
    ]),
    [],
    [t("rapport.total"), "", Math.round(totalFabricationKg), ""],
  ]
  utils.book_append_sheet(
    wb,
    utils.aoa_to_sheet(fabRows),
    t("rapport.sheets.fabrication"),
  )

  // Per-planned-lot Freinte reconciliation sheet.
  const freinteRowsAoa = [
    [
      t("rapport.freinteCols.plannedLot"),
      t("rapport.freinteCols.lotNumber"),
      `${t("rapport.freinteCols.plannedKg")} (kg)`,
      `${t("rapport.freinteCols.actualKg")} (kg)`,
      `${t("rapport.freinteCols.ecartKg")} (kg)`,
    ],
    ...f.rows.map((r) => [
      r.plannedLabel,
      r.fabricatedLotNumber ?? "",
      Math.round(r.plannedKg),
      r.actualKg != null ? Math.round(r.actualKg) : "",
      r.ecartKg != null ? Math.round(r.ecartKg) : "",
    ]),
    [],
    [
      t("rapport.freinteSummary.totalPlanned"),
      "",
      Math.round(f.summary.totalPlannedKg),
    ],
    [
      t("rapport.freinteSummary.totalActual"),
      "",
      Math.round(f.summary.totalActualKg),
    ],
    [
      t("rapport.freinteSummary.waste"),
      "",
      Math.round(f.summary.wasteKg),
    ],
    [
      t("rapport.freinteSummary.ecart"),
      "",
      Math.round(f.summary.ecartKg),
    ],
    [
      `${t("rapport.freinteSummary.freinte")} (%)`,
      "",
      Number(f.summary.freintePct.toFixed(2)),
    ],
  ]
  utils.book_append_sheet(
    wb,
    utils.aoa_to_sheet(freinteRowsAoa),
    t("rapport.sheets.freinte"),
  )

  writeFile(wb, `${rapport.usinageNumber}-usinage.xlsx`)
}
